import { navigatorLock, processLock } from "@supabase/supabase-js"

// Browsers that block the site's data (Chrome's "Not allowed to save data"
// cookie setting, some Android WebViews) also deny the Web Locks API:
// `navigator.locks.request` rejects with "SecurityError: The request was
// denied.". supabase-js takes that lock for every auth call, so restoring the
// session and signing in failed, and the rejection went unhandled (Sentry
// PRODUCTION-F). With storage blocked the session only lives in this page's
// memory, so there's no other tab to coordinate with: the in-page lock is
// enough.

type Lock = <R>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>,
) => Promise<R>

function isSecurityError(error: unknown): boolean {
  return (
    !!error &&
    typeof error === "object" &&
    (error as { name?: unknown }).name === "SecurityError"
  )
}

/**
 * A supabase-js auth lock: the browser's Web Locks API, or an in-page lock
 * once the browser denies it.
 */
export function createAuthLock(
  primary: Lock = navigatorLock,
  fallback: Lock = processLock,
): Lock {
  let isDenied = typeof navigator === "undefined" || !navigator.locks

  return async (name, acquireTimeout, fn) => {
    if (isDenied) return fallback(name, acquireTimeout, fn)

    let didRun = false

    try {
      return await primary(name, acquireTimeout, () => {
        didRun = true

        return fn()
      })
    } catch (error) {
      // NOTE: Only when the lock itself was denied: a SecurityError from `fn`
      // (e.g. blocked storage) is its own error, and `fn` mustn't run twice.
      if (didRun || !isSecurityError(error)) throw error

      isDenied = true

      return fallback(name, acquireTimeout, fn)
    }
  }
}
